import { useState, useCallback } from 'react'
import { useFtChatbotChat } from '../../hooks/api/mutations/ft-chatbot/useFtChatbotChat'
import { useFtChatbotConfirm } from '../../hooks/api/mutations/ft-chatbot/useFtChatbotConfirm'
import { useLanguage } from '@context'
import { translateApiError } from '@utils'

export interface PendingAction {
  toolName: string
  args: Record<string, unknown>
  description: string
}

export interface ChatMessage {
  id: string
  role: 'user' | 'model'
  content: string
  timestamp: string
  pendingActions?: PendingAction[]
}

export const useChatbot = () => {
  const { t } = useLanguage()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputText, setInputText] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  const [isThinking, setIsThinking] = useState(false)

  const chatMutation = useFtChatbotChat()
  const confirmMutation = useFtChatbotConfirm()

  const sendMessage = useCallback(
    (text?: string) => {
      const messageText = text || inputText.trim()
      if (!messageText || isThinking) return

      const userMsg: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: messageText,
        timestamp: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, userMsg])
      setInputText('')
      setIsThinking(true)

      chatMutation.mutate(
        { message: messageText, conversationId },
        {
          onSuccess: (data) => {
            const aiMsg: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'model',
              content: data.response,
              timestamp: new Date().toISOString(),
              pendingActions: data.pendingActions,
            }
            setMessages((prev) => [...prev, aiMsg])
            setConversationId(data.conversationId)
            if (data.pendingActions && data.pendingActions.length > 0) {
              setPendingActions(data.pendingActions)
            }
            setIsThinking(false)
          },
          onError: (error) => {
            const errorMsg: ChatMessage = {
              id: (Date.now() + 1).toString(),
              role: 'model',
              content: translateApiError(t, error).message,
              timestamp: new Date().toISOString(),
            }
            setMessages((prev) => [...prev, errorMsg])
            setIsThinking(false)
          },
        },
      )
    },
    [inputText, isThinking, conversationId, chatMutation],
  )

  const confirmAction = useCallback(() => {
    if (pendingActions.length === 0 || !conversationId) return

    setIsThinking(true)

    confirmMutation.mutate(
      {
        conversationId,
        actions: pendingActions.map((a) => ({ toolName: a.toolName, args: a.args })),
      },
      {
        onSuccess: (data) => {
          const confirmMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'model',
            content: data.response,
            timestamp: new Date().toISOString(),
            pendingActions: data.pendingActions,
          }
          setMessages((prev) => [...prev, confirmMsg])
          // If the continuation triggered more write tools, show them
          if (data.pendingActions && data.pendingActions.length > 0) {
            setPendingActions(data.pendingActions)
          } else {
            setPendingActions([])
          }
          setIsThinking(false)
        },
        onError: (error) => {
          const errorMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'model',
            content: translateApiError(t, error).message,
            timestamp: new Date().toISOString(),
          }
          setMessages((prev) => [...prev, errorMsg])
          setPendingActions([])
          setIsThinking(false)
        },
      },
    )
  }, [pendingActions, conversationId, confirmMutation])

  const cancelAction = useCallback(() => {
    const cancelMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'model',
      content: t('chatbot.actionCancelled'),
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, cancelMsg])
    setPendingActions([])
  }, [t])

  const startNewConversation = useCallback(() => {
    setMessages([])
    setConversationId(undefined)
    setPendingActions([])
    setInputText('')
    setIsThinking(false)
  }, [])

  return {
    messages,
    inputText,
    setInputText,
    isThinking,
    pendingActions,
    sendMessage,
    confirmAction,
    cancelAction,
    startNewConversation,
  }
}
